'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useId } from 'react';
import { useFieldContext } from '@/lib/form/contexts';

interface SelectFieldProps {
  label: string;
  options: readonly { value: string; label: string }[];
  required?: boolean;
}

/**
 * Exported so forms outside this component's framework can share it. This
 * component is bound to TanStack Form (`useFieldContext`), but the Tutor
 * Application Form is reducer-based and cannot mount it — it imports the class
 * instead. Same arrangement as `formInputBaseClass` in `FormInput.tsx`.
 *
 * `text-base` below `md`, not `text-sm`: Mobile Safari zooms the viewport when a
 * focused control's font-size is under 16px, and never zooms back (SH-480). The
 * design's 14px returns from `md` up, where no browser does this.
 */
export const formSelectBaseClass =
  'block h-[var(--input-height,55px)] w-full appearance-none rounded-input border bg-[#eae8e7] px-4 py-1 text-base md:text-sm text-foreground outline-none duration-100 focus:border-[rgba(40,25,10,0.2)] disabled:cursor-not-allowed disabled:opacity-50';

/**
 * The dropdown affordance, exported alongside the class for the same reason.
 *
 * `formSelectBaseClass` is `appearance-none`, which strips the native arrow —
 * so anything using that class has to draw this one, or the control stops
 * looking like a select at all. Shipping them together is what stops a consumer
 * taking the class and losing the arrow.
 *
 * Absolutely positioned: the caller wraps the `<select>` in a `relative`
 * element and pads it with `pr-10` to keep the text clear of it.
 */
export const SelectChevron = () => (
  <svg
    className="text-foreground/60 pointer-events-none absolute top-1/2 right-4 -translate-y-1/2"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4 6l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const SelectField = ({ label, options, required }: SelectFieldProps) => {
  const field = useFieldContext<string>();
  const id = useId();
  const error = field.state.meta.errors[0]?.message;
  const errorId = `${id}-error`;

  return (
    <div>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <div className="relative">
        <select
          id={id}
          name={field.name}
          value={field.state.value}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          className={classNames(
            formSelectBaseClass,
            'pr-10',
            !field.state.value && 'text-foreground/60',
            error ? 'border-red-400' : 'border-[#eae8e7]',
          )}
        >
          <option value="">
            {label}
            {required ? ' *' : ''}
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <SelectChevron />
      </div>
      {error && (
        <p id={errorId} className="m-0 mt-1 pl-4 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};
